import React, { useCallback, useEffect, useState } from "react";
import { Layout, Modal } from "antd";
import type { UploadFile } from "antd";
import { useTranslation } from "react-i18next";
import * as kioskApi from "./services/kioskApi";
import "./SelfSignIn.scss";
import { OpButton } from "components/customAntd/DLS/OpButton/OpButton";
import { OpForm } from "components/customAntd/DLS/OpForm/OpForm";
import Loader from "components/customAntd/Loader";
import SelectionPage from "./pages/SelectionPage";
import VisitorTypeSelectionPage, {
  handleVisitorTypeSelectionNext,
} from "./pages/VisitorTypeSelectionPage";
import SignInPage, { handleSignInNext } from "./pages/SignInPage";
import ScheduledVisitorSelectionPage, {
  handleScheduledVisitorSelectionNext,
} from "./pages/ScheduledVisitorSelectionPage";
import VisitDetailsPage, {
  handleVisitDetailsNext,
} from "./pages/VisitDetailsPage";
import AgreementsPage, { handleAgreementsNext } from "./pages/AgreementsPage";
import PhotoCapturePage from "./pages/PhotoCapturePage";
import SignOutPage, { handleSignOutNext } from "./pages/SignOutPage";
import SignedInVisitorSelectionPage, {
  handleSignedInVisitorSelectionNext,
} from "./pages/SignedInVisitorSelectionPage";
import RedFlagDeniedPage from "./pages/RedFlagDeniedPage";
import ConfirmationPage from "./pages/ConfirmationPage";
import DisabledPage from "./pages/DisabledPage";
import { generateAgreementPDF } from "./utils/pdfGenerator";

const { Content } = Layout;

type SignInOutStep =
  | "selection"
  | "signIn"
  | "scheduledVisitorSelection"
  | "visitorTypeSelection"
  | "visitDetails"
  | "agreements"
  | "photoCapture"
  | "signOut"
  | "signedInVisitorSelection"
  | "confirmation"
  | "redFlagDenied"
  | "disabled";

const SelfSignIn: React.FC = () => {
  const { t, i18n } = useTranslation();
  const STORAGE_KEY = "selfSignIn";
  const LANGUAGE_STORAGE_KEY = "selfSignIn_language";

  const [isLoading, setIsLoading] = useState(true);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const hasInitialized = React.useRef(false);
  const [logoUrl, setLogoUrl] = useState<string>(
    "/images/Alta_Visitor_Vertical_Blue.png",
  );
  const [backgroundUrl, setBackgroundUrl] = useState<string | null>(null);
  const [token, setToken] = useState<string>("");
  const [locationId, setLocationId] = useState<number>(0);
  const [currentStep, setCurrentStep] = useState<SignInOutStep | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>("");
  const [selectedVisitorType, setSelectedVisitorType] = useState<string | null>(
    null,
  );
  const [scheduledVisitors, setScheduledVisitors] = useState<any[]>([]);
  const [selectedScheduledVisitor, setSelectedScheduledVisitor] = useState<
    any | null
  >(null);
  const [isNameNotOnList, setIsNameNotOnList] = useState(false);
  const [signedInVisitors, setSignedInVisitors] = useState<any[]>([]);
  const [selectedSignedInVisitor, setSelectedSignedInVisitor] = useState<
    any | null
  >(null);
  const [isSignOutFlow, setIsSignOutFlow] = useState(false);
  const [agreedAgreements, setAgreedAgreements] = useState<string[]>([]);
  const [uploadedPhoto, setUploadedPhoto] = useState<UploadFile | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [customFieldsData, setCustomFieldsData] = useState<any[]>([]);
  const [activeVisitorTypeId, setActiveVisitorTypeId] = useState<string | null>(
    null,
  );
  const [signInFirstName, setSignInFirstName] = useState<string>("");
  const [signInLastName, setSignInLastName] = useState<string>("");
  const [signInMiddleName, setSignInMiddleName] = useState<string>("");

  // Workflow configuration from API
  const [workflowByVisitorType, setWorkflowByVisitorType] = useState<any[]>([]);
  const [workflowInConfig, setWorkflowInConfig] = useState<any>(null);
  const [workflowOutConfig, setWorkflowOutConfig] = useState<any>(null);
  const [workflowInFields, setWorkflowInFields] = useState<any>(null);
  const [workflowInFieldsCustom, setWorkflowInFieldsCustom] = useState<any[]>(
    [],
  );
  const [agreements, setAgreements] = useState<any[]>([]);
  const [allowSelfSignIn, setAllowSelfSignIn] = useState<number>(1);
  const [allowSelfSignOut, setAllowSelfSignOut] = useState<number>(1);
  const [hosts, setHosts] = useState<any[]>([]);
  const [visitDetailsAutoSkipped, setVisitDetailsAutoSkipped] = useState<
    boolean | null
  >(null);

  const normalizeNameValues = (values: {
    firstName: string;
    lastName: string;
    middleName?: string;
  }) => ({
    ...values,
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    middleName: values.middleName?.trim(),
  });

  // Store default configs for reset
  const defaultConfigsRef = React.useRef<{
    workflowInConfig: any;
    workflowOutConfig: any;
    workflowInFields: any;
    workflowInFieldsCustom: any[];
    agreements: any[];
  } | null>(null);

  const [visitorTypes, setVisitorTypes] = useState<any[]>([]);

  const [form] = OpForm.useForm();
  const [signOutForm] = OpForm.useForm();
  const [visitorTypeForm] = OpForm.useForm();
  const [visitDetailsForm] = OpForm.useForm();

  // Load saved language on mount - independent from login page language
  useEffect(() => {
    const savedLanguage = sessionStorage.getItem(LANGUAGE_STORAGE_KEY);
    const languageToUse = savedLanguage || "en"; // Default to English if no saved language

    setSelectedLanguage(languageToUse);
    // Force language change immediately, overriding any login page language
    i18n.changeLanguage(languageToUse);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Load data from localStorage on mount
  useEffect(() => {
    // Prevent double initialization in React Strict Mode
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    const initializeApp = async () => {
      try {
        // Parse URL: ?{token} or ?{token}&step=... or ?step=...&{token}
        const fullSearch = window.location.search;

        let tokenParam = "";

        if (fullSearch) {
          const searchWithoutQuestion = fullSearch.substring(1); // Remove '?'

          // Check if there's a step parameter
          const stepMatch = searchWithoutQuestion.match(/[&]?step=([^&]+)/);

          if (stepMatch) {
            // URL has step parameter - extract token by removing step param
            tokenParam = searchWithoutQuestion
              .replace(/[&]?step=[^&]+/, "") // Remove step param
              .replace(/^&/, "") // Remove leading & if any
              .replace(/&$/, ""); // Remove trailing & if any
          } else {
            // No step parameter - entire string is token
            tokenParam = searchWithoutQuestion;
          }
        }

        if (!tokenParam) {
          setCurrentStep("disabled");
          window.history.replaceState({}, "", `/selfSignIn?step=disabled`);
          setIsLoading(false);
          return;
        }

        // Validate token
        const validationResult = await kioskApi.validateToken(tokenParam);

        if (!validationResult.isValid) {
          setCurrentStep("disabled");
          window.history.replaceState(
            {},
            "",
            `/selfSignIn?${tokenParam}&step=disabled`,
          );
          setIsLoading(false);
          return;
        }

        setTokenError(null); // Clear any previous error
        setToken(tokenParam);

        // Fetch logo content
        try {
          const logoContent = await kioskApi.fetchLogoContent(tokenParam);
          if (logoContent) {
            setLogoUrl(logoContent);
          }
          // If no logo returned, keep default logo
        } catch (error) {
          console.error("Failed to fetch logo content:", error);
          // Keep default logo on error
        }

        // Extract locationId from token payload
        try {
          const payloadBase64 = tokenParam.split(".")[1];
          const payload = JSON.parse(atob(payloadBase64));
          const locationIdFromToken =
            payload.locationId || payload.data?.locationId || 0;
          setLocationId(locationIdFromToken);
        } catch (error) {
          console.error("Failed to extract locationId from token:", error);
        }

        // Save token to localStorage immediately before URL cleanup
        const existingStorage = sessionStorage.getItem(STORAGE_KEY);
        let storageData: any = {};
        if (existingStorage) {
          try {
            storageData = JSON.parse(existingStorage);
          } catch (error) {
            console.error("Error parsing localStorage:", error);
          }
        }
        storageData.token = tokenParam;
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));

        // Token stays in URL - no cleanup needed

        // Check if URL has step parameter
        const urlParams = new URLSearchParams(window.location.search);
        const stepParam = urlParams.get("step");

        // If step=signIn or step=signOut, clear localStorage to start fresh with default settings
        if (stepParam === "signIn" || stepParam === "signOut") {
          sessionStorage.removeItem(STORAGE_KEY);
          // Save only the token back to localStorage
          const freshStorage = { token: tokenParam };
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(freshStorage));
        }

        // Load from localStorage FIRST before any state changes
        const savedData = sessionStorage.getItem(STORAGE_KEY);
        let savedActiveVisitorTypeId: string | null = null;

        if (savedData) {
          try {
            const data = JSON.parse(savedData);

            // Check if the saved token matches the current token
            if (data.token && data.token !== tokenParam) {
              sessionStorage.removeItem(STORAGE_KEY);
              // Don't restore any data
            } else {
              // Token matches - restore user input data only (not workflow configs)
              if (data.selectedVisitorType)
                setSelectedVisitorType(data.selectedVisitorType);
              if (data.scheduledVisitors)
                setScheduledVisitors(data.scheduledVisitors);
              if (data.selectedScheduledVisitor)
                setSelectedScheduledVisitor(data.selectedScheduledVisitor);
              if (data.isNameNotOnList !== undefined)
                setIsNameNotOnList(data.isNameNotOnList);
              if (data.signedInVisitors)
                setSignedInVisitors(data.signedInVisitors);
              if (data.selectedSignedInVisitor)
                setSelectedSignedInVisitor(data.selectedSignedInVisitor);
              if (data.isSignOutFlow !== undefined)
                setIsSignOutFlow(data.isSignOutFlow);
              if (data.agreedAgreements)
                setAgreedAgreements(data.agreedAgreements);
              // NOTE: uploadedPhoto is NOT restored from localStorage (to avoid quota issues)
              if (data.activeVisitorTypeId) {
                savedActiveVisitorTypeId = data.activeVisitorTypeId;
                setActiveVisitorTypeId(data.activeVisitorTypeId);
              }
              if (data.customFieldsData)
                setCustomFieldsData(data.customFieldsData);
              if (data.signInFirstName)
                setSignInFirstName(data.signInFirstName);
              if (data.signInLastName) setSignInLastName(data.signInLastName);
              if (data.signInMiddleName)
                setSignInMiddleName(data.signInMiddleName);

              // NOTE: Workflow configurations are NOT restored from localStorage
              // They are always fetched fresh from API to ensure latest settings
            }
          } catch (error) {
            console.error("Error loading saved data:", error);
          }
        }

        // Fetch all configuration from APIs in parallel using tokenParam (not token state)
        const [
          workflowInConfigData,
          workflowInFieldsData,
          workflowInFieldsCustomData,
          agreementsData,
          workflowByVisitorTypeData,
          hostsData,
        ] = await Promise.all([
          kioskApi.getWorkflowInConfig(tokenParam),
          kioskApi.getWorkflowInFields(tokenParam),
          kioskApi.getWorkflowInFieldsCustom(tokenParam),
          kioskApi.getLocationAgreements(tokenParam),
          kioskApi.getWorkflowByVisitorType(tokenParam),
          kioskApi.getHosts(tokenParam),
        ]);

        // Store workflowByVisitorType and hosts
        setWorkflowByVisitorType(workflowByVisitorTypeData);

        // Transform hosts data to dropdown format
        if (hostsData && Array.isArray(hostsData) && hostsData.length > 0) {
          const formattedHosts = hostsData
            .map((host: any) => ({
              label:
                `${host.firstName || ""} ${host.lastName || ""}`.trim() ||
                t("selfSignIn.error.unknown"),
              value: host.userId?.toString() || "",
              email: host.email,
              department: host.department,
              title: host.title,
            }))
            .filter((host: any) => host.value);
          setHosts(formattedHosts);
        } else {
          setHosts([]);
        }

        // ALWAYS store default configs for later reset (regardless of localStorage restoration)
        const workflowIn = workflowInConfigData?.workflowIn?.[0];
        const workflowOut = workflowInConfigData?.workflowOut?.[0];
        const defaultFormattedAgreements = agreementsData
          .filter((ag: any) => ag.forSignIn === 1)
          .map((agreement: any) => ({
            id: agreement.id?.toString() || "",
            name: agreement.name || "",
          }));

        defaultConfigsRef.current = {
          workflowInConfig: workflowIn || workflowInConfigData,
          workflowOutConfig: workflowOut || null,
          workflowInFields: workflowInFieldsData,
          workflowInFieldsCustom: workflowInFieldsCustomData,
          agreements: defaultFormattedAgreements,
        };

        // ALWAYS apply config from API (do not use localStorage for workflow configs)
        // Check if we need to override with visitor-type-specific workflow
        let shouldUseDefault = true;
        if (savedActiveVisitorTypeId && workflowByVisitorTypeData.length > 0) {
          const config = workflowByVisitorTypeData.find(
            (item: any) =>
              item.visitorType?.id?.toString() === savedActiveVisitorTypeId,
          );

          if (config) {
            shouldUseDefault = false;

            // Override with visitor-type-specific configuration
            if (config.workflowInConfig) {
              setWorkflowInConfig(config.workflowInConfig);
            } else {
              const workflowIn = workflowInConfigData?.workflowIn?.[0];
              setWorkflowInConfig(workflowIn || workflowInConfigData);
            }

            if (config.workflowOutConfig) {
              setWorkflowOutConfig(config.workflowOutConfig);
            } else {
              const workflowOut = workflowInConfigData?.workflowOut?.[0];
              setWorkflowOutConfig(workflowOut || null);
            }

            if (config.workflowField) {
              setWorkflowInFields(config.workflowField);
            } else {
              setWorkflowInFields(workflowInFieldsData);
            }

            if (config.customWorkFlowField) {
              setWorkflowInFieldsCustom(config.customWorkFlowField);
            } else {
              setWorkflowInFieldsCustom(workflowInFieldsCustomData);
            }

            if (config.agreements) {
              const formattedAgreements = config.agreements
                .filter((ag: any) => ag.document?.forSignIn === 1)
                .map((agreement: any) => ({
                  id: agreement.document?.id?.toString() || "",
                  name: agreement.document?.name || "",
                  content: agreement.content || "",
                  type: agreement.document?.type,
                }));
              setAgreements(formattedAgreements);
            } else {
              // Format default agreements
              const formattedAgreements = agreementsData
                .filter((ag: any) => ag.forSignIn === 1)
                .map((agreement: any) => ({
                  id: agreement.id?.toString() || "",
                  name: agreement.name || "",
                }));
              setAgreements(formattedAgreements);
            }
          }
        }

        // Use default workflow if no override
        if (shouldUseDefault) {
          setWorkflowInConfig(workflowIn || workflowInConfigData);
          setWorkflowOutConfig(workflowOut || null);

          setWorkflowInFields(workflowInFieldsData);
          setWorkflowInFieldsCustom(workflowInFieldsCustomData);
          setAgreements(defaultFormattedAgreements);
        }

        // Always set self sign in/out flags from API (not localStorage)
        // These should reflect the latest configuration from the server
        setAllowSelfSignIn(workflowInConfigData?.selfSignIn ?? 1);
        setAllowSelfSignOut(workflowInConfigData?.selfSignOut ?? 1);

        // Extract appearance settings from workflowInConfig
        if (workflowInConfigData?.appearance) {
          const { logoUrl: apiLogoUrl, backgroundUrl: apiBackgroundUrl } =
            workflowInConfigData.appearance;

          if (apiLogoUrl) setLogoUrl(apiLogoUrl);
          if (apiBackgroundUrl) setBackgroundUrl(apiBackgroundUrl);
        }

        // Initially, don't show any visitor types until walk-in flow is triggered
        setVisitorTypes([]);

        // Restore form values and visitor types from localStorage
        if (savedData) {
          try {
            const data = JSON.parse(savedData);
            const forms = data.forms || {};
            if (forms.signIn) {
              form.setFieldsValue(forms.signIn);
              // Also restore firstName/lastName/middleName to state for CheckrTrust and red flag check
              if (forms.signIn.firstName)
                setSignInFirstName(forms.signIn.firstName);
              if (forms.signIn.lastName)
                setSignInLastName(forms.signIn.lastName);
              if (forms.signIn.middleName)
                setSignInMiddleName(forms.signIn.middleName);
            }
            if (forms.signOut) signOutForm.setFieldsValue(forms.signOut);
            if (forms.visitorType)
              visitorTypeForm.setFieldsValue(forms.visitorType);
            if (forms.visitDetails)
              visitDetailsForm.setFieldsValue(forms.visitDetails);
            // Restore visitor types if available
            if (data.visitorTypes && Array.isArray(data.visitorTypes)) {
              setVisitorTypes(data.visitorTypes);
            }
          } catch (error) {
            console.error("Error loading saved form data:", error);
          }
        }

        // Determine initial step based on configuration
        // stepParam was already extracted above
        if (
          stepParam &&
          [
            "selection",
            "signIn",
            "scheduledVisitorSelection",
            "visitorTypeSelection",
            "visitDetails",
            "agreements",
            "photoCapture",
            "signOut",
            "signedInVisitorSelection",
            "confirmation",
            "redFlagDenied",
            "disabled",
          ].includes(stepParam)
        ) {
          // Check if this is a middle step that requires previous data
          const middleSteps = [
            "scheduledVisitorSelection",
            "visitorTypeSelection",
            "visitDetails",
            "agreements",
            "photoCapture",
            "signedInVisitorSelection",
          ];

          if (middleSteps.includes(stepParam)) {
            // Middle step requires sessionStorage data with matching token and form data
            let hasValidData = false;

            if (savedData) {
              try {
                const data = JSON.parse(savedData);
                // Check if token matches AND has form data
                if (
                  data.token &&
                  data.token === tokenParam &&
                  data.forms &&
                  Object.keys(data.forms).length > 0
                ) {
                  hasValidData = true;
                }
              } catch (error) {
                // Invalid data
                hasValidData = false;
              }
            }

            if (!hasValidData) {
              // No valid saved data - redirect to appropriate starting page
              const onlySignInEnabled =
                (workflowInConfigData?.selfSignIn ?? 1) === 1 &&
                (workflowInConfigData?.selfSignOut ?? 1) === 0;
              const onlySignOutEnabled =
                (workflowInConfigData?.selfSignIn ?? 1) === 0 &&
                (workflowInConfigData?.selfSignOut ?? 1) === 1;

              let redirectUrl = `/selfSignIn?${tokenParam}`;
              if (onlySignInEnabled) {
                redirectUrl = `/selfSignIn?${tokenParam}&step=signIn`;
              } else if (onlySignOutEnabled) {
                redirectUrl = `/selfSignIn?${tokenParam}&step=signOut`;
              }

              // Keep loading state active during redirect
              setIsLoading(true);
              // Use window.location to force a redirect and reload
              window.location.href = redirectUrl;
              return; // Stop further execution
            } else {
              // Has valid saved data - allow access to middle step
              setCurrentStep(stepParam as SignInOutStep);
            }
          } else {
            // Starting page or special page - allow direct access
            setCurrentStep(stepParam as SignInOutStep);
          }
        } else {
          // First time accessing - determine initial step
          const signInEnabled = (workflowInConfigData?.selfSignIn ?? 1) === 1;
          const signOutEnabled = (workflowInConfigData?.selfSignOut ?? 1) === 1;

          if (!signInEnabled && !signOutEnabled) {
            // Both disabled - show disabled message
            setCurrentStep("disabled");
          } else {
            // Always show selection page regardless of enabled options
            setCurrentStep("selection");
          }
        }
      } catch (error) {
        console.error("Error initializing app:", error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeApp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update URL when step changes and restore form values
  useEffect(() => {
    // Skip URL update during initial load or if step is not set
    if (isLoading || !currentStep || !token) {
      return;
    }

    // Update URL while preserving token
    if (currentStep !== "selection" && currentStep !== "disabled") {
      // Add step parameter: ?{token}&step={currentStep}
      window.history.replaceState(
        {},
        "",
        `/selfSignIn?${token}&step=${currentStep}`,
      );
    } else {
      // Selection page or disabled page - only token: ?{token}
      // This allows browser reload on disabled page to reinitialize
      window.history.replaceState({}, "", `/selfSignIn?${token}`);
    }

    // Clear localStorage when reaching confirmation page
    if (currentStep === "confirmation") {
      sessionStorage.removeItem(STORAGE_KEY);
    }

    // Restore form values for the current step (with delay to ensure form is rendered)
    const savedData = sessionStorage.getItem(STORAGE_KEY);
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        const formData = data.forms || {};

        // Use setTimeout to ensure form is mounted before setting values
        setTimeout(() => {
          switch (currentStep) {
            case "signIn":
              if (formData.signIn) {
                form.setFieldsValue(formData.signIn);
              }
              break;
            case "signOut":
              if (formData.signOut) {
                signOutForm.setFieldsValue(formData.signOut);
              }
              break;
            case "visitorTypeSelection":
              if (formData.visitorType) {
                visitorTypeForm.setFieldsValue(formData.visitorType);
              }
              break;
            case "visitDetails":
              if (formData.visitDetails) {
                visitDetailsForm.setFieldsValue(formData.visitDetails);
              }
              break;
          }
        }, 0);
      } catch (error) {
        console.error("Error restoring form data for step:", error);
      }
    }
  }, [
    currentStep,
    isLoading,
    token,
    form,
    signOutForm,
    visitorTypeForm,
    visitDetailsForm,
  ]);

  // Save data to localStorage when it changes (but not on initial mount)
  useEffect(() => {
    // Don't save during initial loading or if there's a token error
    if (isLoading || tokenError) {
      return;
    }

    // Get existing data to preserve forms
    const existing = sessionStorage.getItem(STORAGE_KEY);
    let existingData: any = {};
    if (existing) {
      try {
        existingData = JSON.parse(existing);
      } catch (error) {
        console.error("Error parsing existing data:", error);
      }
    }

    const dataToSave = {
      ...existingData,
      token,
      selectedVisitorType,
      visitorTypes,
      scheduledVisitors,
      selectedScheduledVisitor,
      isNameNotOnList,
      signedInVisitors,
      selectedSignedInVisitor,
      isSignOutFlow,
      agreedAgreements,
      // NOTE: uploadedPhoto is NOT saved to localStorage to avoid quota exceeded errors
      // Photo is kept in memory only and will be uploaded when form is submitted
      activeVisitorTypeId,
      customFieldsData,
      signInFirstName,
      signInLastName,
      signInMiddleName,
      // Save workflow configurations
      workflowInConfig,
      workflowOutConfig,
      workflowInFields,
      workflowInFieldsCustom,
      agreements,
      allowSelfSignIn,
      allowSelfSignOut,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
  }, [
    isLoading,
    tokenError,
    token,
    selectedVisitorType,
    visitorTypes,
    scheduledVisitors,
    selectedScheduledVisitor,
    isNameNotOnList,
    signedInVisitors,
    selectedSignedInVisitor,
    isSignOutFlow,
    agreedAgreements,
    activeVisitorTypeId,
    customFieldsData,
    signInFirstName,
    signInLastName,
    signInMiddleName,
    workflowInConfig,
    workflowOutConfig,
    workflowInFields,
    workflowInFieldsCustom,
    agreements,
    allowSelfSignIn,
    allowSelfSignOut,
  ]);

  // Validate URL step parameter against allowed flows
  useEffect(() => {
    if (!isLoading) {
      const urlParams = new URLSearchParams(window.location.search);
      const stepParam = urlParams.get("step");

      if (stepParam) {
        const isSignInStep = [
          "signIn",
          "scheduledVisitorSelection",
          "visitorTypeSelection",
          "visitDetails",
          "agreements",
          "photoCapture",
        ].includes(stepParam);
        const isSignOutStep = ["signOut", "signedInVisitorSelection"].includes(
          stepParam,
        );

        // Block sign-in steps if sign-in is disabled
        if (isSignInStep && allowSelfSignIn === 0) {
          if (allowSelfSignOut === 1) {
            // Sign-out is enabled - go to selection page
            setCurrentStep("selection");
            if (token) {
              window.history.replaceState({}, "", `/selfSignIn?${token}`);
            }
          } else {
            // Both disabled
            setCurrentStep("disabled");
            if (token) {
              window.history.replaceState(
                {},
                "",
                `/selfSignIn?${token}&step=disabled`,
              );
            }
          }
          return;
        }

        // Block sign-out steps if sign-out is disabled
        if (isSignOutStep && allowSelfSignOut === 0) {
          if (allowSelfSignIn === 1) {
            // Sign-in is enabled - go to selection page
            setCurrentStep("selection");
            if (token) {
              window.history.replaceState({}, "", `/selfSignIn?${token}`);
            }
          } else {
            // Both disabled
            setCurrentStep("disabled");
            if (token) {
              window.history.replaceState(
                {},
                "",
                `/selfSignIn?${token}&step=disabled`,
              );
            }
          }
          return;
        }
      }
    }
  }, [isLoading, token, allowSelfSignIn, allowSelfSignOut]);

  // Reset selection when entering scheduledVisitorSelection or signedInVisitorSelection page
  useEffect(() => {
    if (currentStep === "scheduledVisitorSelection") {
      // Clear all form data when entering visitor selection page
      visitDetailsForm.resetFields();
      setCustomFieldsData([]);
      setAgreedAgreements([]);
      setUploadedPhoto(null);

      // Clear visitor selection
      setSelectedScheduledVisitor(null);
      setIsNameNotOnList(false);

      // Clear from sessionStorage
      const existingStorageData = sessionStorage.getItem(STORAGE_KEY);
      if (existingStorageData) {
        try {
          const storageData = JSON.parse(existingStorageData);
          // Clear all form data
          if (storageData.forms) {
            delete storageData.forms.visitDetails;
          }
          // Clear other data
          storageData.agreedAgreements = [];
          storageData.customFieldsData = [];
          storageData.selectedScheduledVisitor = null;
          storageData.isNameNotOnList = false;
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
        } catch (error) {
          console.error("Error clearing data from storage:", error);
        }
      }
    } else if (currentStep === "signedInVisitorSelection") {
      // Clear signed-in visitor selection
      setSelectedSignedInVisitor(null);

      // Clear from sessionStorage
      const existingStorageData = sessionStorage.getItem(STORAGE_KEY);
      if (existingStorageData) {
        try {
          const storageData = JSON.parse(existingStorageData);
          storageData.selectedSignedInVisitor = null;
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
        } catch (error) {
          console.error("Error clearing data from storage:", error);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Auto-skip visitDetails if all required fields are already filled with INITIAL VALUES
  useEffect(() => {
    // Only auto-skip if:
    // 1. On visitDetails page
    // 2. Not loading or submitting
    // 3. Haven't processed yet (null)
    // 4. Has a scheduled visitor (initial values exist)
    if (
      currentStep !== "visitDetails" ||
      isLoading ||
      isSubmitting ||
      visitDetailsAutoSkipped !== null
    ) {
      return;
    }

    // Only check for auto-skip if there's a scheduled visitor with initial data
    if (!selectedScheduledVisitor) {
      // No scheduled visitor = walk-in = no initial values = never auto-skip
      setVisitDetailsAutoSkipped(false);
      return;
    }

    // Check if all required fields are filled
    const checkAndSkipIfFilled = async () => {
      try {
        // Get form values
        const values = visitDetailsForm.getFieldsValue();

        // Check standard required fields
        const requiredFieldsFilled: boolean[] = [];

        if (
          workflowInFields?.email?.included === 1 &&
          workflowInFields.email.required === 1
        ) {
          requiredFieldsFilled.push(!!values.email);
        }
        if (
          workflowInFields?.visitCompany?.included === 1 &&
          workflowInFields.visitCompany.required === 1
        ) {
          requiredFieldsFilled.push(!!values.company);
        }
        if (
          workflowInFields?.visitPhone?.included === 1 &&
          workflowInFields.visitPhone.required === 1
        ) {
          requiredFieldsFilled.push(!!values.phone);
        }
        if (
          workflowInFields?.host?.included === 1 &&
          workflowInFields.host.required === 1
        ) {
          requiredFieldsFilled.push(!!values.host);
        }
        if (
          workflowInFields?.visitPurpose?.included === 1 &&
          workflowInFields.visitPurpose.required === 1
        ) {
          requiredFieldsFilled.push(!!values.purpose);
        }

        // Check custom required fields
        const customRequiredFields = workflowInFieldsCustom.filter(
          (field: any) => field.included === 1 && field.required === 1,
        );

        for (const field of customRequiredFields) {
          const fieldName = `customField_${field.id}`;
          requiredFieldsFilled.push(!!values[fieldName]);
        }

        // If all required fields are filled, auto-skip
        const allFilled =
          requiredFieldsFilled.length > 0 &&
          requiredFieldsFilled.every((filled) => filled);

        if (allFilled) {
          setVisitDetailsAutoSkipped(true);

          // Save form values
          const result = await handleVisitDetailsNext(visitDetailsForm);
          saveFormValue("visitDetails", result.values);
          setCustomFieldsData(result.customFieldsData || []);

          // Navigate to next step
          await navigateAfterVisitDetails();
        } else {
          setVisitDetailsAutoSkipped(false);
        }
      } catch (error) {
        // If any error occurs (e.g., validation), don't auto-skip
        setVisitDetailsAutoSkipped(false);
      }
    };

    // Small delay to ensure form is mounted and values are set
    const timer = setTimeout(() => {
      checkAndSkipIfFilled();
    }, 100);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentStep,
    isLoading,
    isSubmitting,
    visitDetailsForm,
    workflowInFields,
    workflowInFieldsCustom,
    selectedScheduledVisitor,
  ]);

  // Scroll to top when step changes
  useEffect(() => {
    if (currentStep) {
      // Scroll the content container to top
      const contentElement = document.querySelector(".self-signin-content");
      if (contentElement) {
        contentElement.scrollTop = 0;
      }
      // Also scroll window to top for mobile landscape
      window.scrollTo(0, 0);
    }
  }, [currentStep]);

  const handleSignIn = () => {
    // Always start with sign in page to search for scheduled visitor
    setIsSignOutFlow(false);
    setCurrentStep("signIn");
  };

  const handleSignOut = () => {
    setIsSignOutFlow(true);
    setCurrentStep("signOut");
  };

  const handleLanguageChange = (language: string) => {
    setSelectedLanguage(language);
    i18n.changeLanguage(language);
    sessionStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  };

  // Save individual form values to localStorage
  const saveFormValue = (formName: string, values: any) => {
    // Don't save if there's a token error
    if (tokenError) {
      return;
    }

    const savedData = sessionStorage.getItem(STORAGE_KEY);
    let allData: any = {};

    if (savedData) {
      try {
        allData = JSON.parse(savedData);
      } catch (error) {
        console.error("Error parsing saved data:", error);
      }
    }

    // Filter out empty values before saving
    const filteredValues = Object.keys(values).reduce((acc: any, key) => {
      const value = values[key];
      // Only save non-empty values
      if (value !== "" && value !== null && value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {});

    // Update the forms object within the main data
    allData.forms = {
      ...(allData.forms || {}),
      [formName]: filteredValues,
    };

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(allData));
  };

  // Final submit function - handles all API calls
  const handleFinalSubmit = useCallback(async (): Promise<{
    success: boolean;
    error?: string;
  }> => {
    try {
      // Helper function to format date to local timezone in YYYY-MM-DD HH:MM:SS format
      const formatLocalDateTime = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const seconds = String(date.getSeconds()).padStart(2, "0");
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      };

      let signInData = form.getFieldsValue();

      // If signIn form is empty, try to get from localStorage
      if (!signInData.firstName || !signInData.lastName) {
        const savedData = sessionStorage.getItem(STORAGE_KEY);
        if (savedData) {
          try {
            const data = JSON.parse(savedData);
            const savedSignInData = data.forms?.signIn;
            if (savedSignInData) {
              signInData = savedSignInData;
            }
          } catch (error) {
            console.error("Error reading signIn from localStorage:", error);
          }
        }
      }

      // Get all field values including untouched ones
      let visitDetails = visitDetailsForm.getFieldsValue(true);

      // Merge with sessionStorage data (sessionStorage as fallback, not override)
      const savedData = sessionStorage.getItem(STORAGE_KEY);
      if (savedData) {
        try {
          const data = JSON.parse(savedData);
          const savedVisitDetails = data.forms?.visitDetails || {};
          // Merge: form values take precedence over saved values
          visitDetails = { ...savedVisitDetails, ...visitDetails };
        } catch (error) {
          console.error("Error reading localStorage:", error);
        }
      }

      // Merge with scheduled visitor data as final fallback
      if (selectedScheduledVisitor) {
        const scheduledVisitorData = {
          email: selectedScheduledVisitor.email,
          company: selectedScheduledVisitor.company,
          phone: selectedScheduledVisitor.phone,
          host: selectedScheduledVisitor.hostId?.toString(),
          purpose: selectedScheduledVisitor.purpose,
        };
        // Merge: current visitDetails takes precedence over scheduled visitor data
        visitDetails = { ...scheduledVisitorData, ...visitDetails };
      }

      // Build visit payload
      const visitPayload: any = {
        locationId: locationId,
      };

      let visitId: number;
      let visitorId: number;
      let visitResponse: any = null;

      if (selectedScheduledVisitor) {
        // Scheduled visitor flow - UPDATE existing visit

        visitId =
          selectedScheduledVisitor.visitId || selectedScheduledVisitor.id;
        visitorId =
          selectedScheduledVisitor.visitorId ||
          selectedScheduledVisitor.visitor?.id ||
          selectedScheduledVisitor.id;

        if (!visitId || !visitorId) {
          throw new Error(
            "Missing visitId or visitorId from scheduled visitor",
          );
        }

        const now = formatLocalDateTime(new Date());

        visitPayload.visitId = visitId;
        visitPayload.status = 2; // Sign In
        visitPayload.signIn = now;
        visitPayload.signOut = null;
        visitPayload.allDay = 0;
        visitPayload.visitorTypeId = selectedVisitorType
          ? parseInt(selectedVisitorType)
          : selectedScheduledVisitor.visitorTypeId || 1;

        // Add visitor info with sign-in status
        // For scheduled visitors, exclude firstName/middleName/lastName - visitor already exists
        visitPayload.visitors = [
          {
            visitorId: visitorId,
            status: 2, // Sign In
            signIn: now,
          },
        ];

        // Add visit details from form
        if (visitDetails) {
          if (
            typeof visitDetails.email === 'string' &&
            visitPayload.visitors?.[0]
          ) {
            visitPayload.visitors[0].email = visitDetails.email.trim();
          }
          if (
            typeof visitDetails.phone === 'string' &&
            visitPayload.visitors?.[0]
          ) {
            visitPayload.visitors[0].mobilePhone = visitDetails.phone.trim();
          }
          if ('company' in visitDetails) {
            visitPayload.company =
              typeof visitDetails.company === 'string'
                ? visitDetails.company.trim()
                : '';
          }
          if ('host' in visitDetails) {
            visitPayload.hostId = visitDetails.host
              ? parseInt(visitDetails.host)
              : -1;
          }
          if ('purpose' in visitDetails) {
            visitPayload.purpose =
              typeof visitDetails.purpose === 'string'
                ? visitDetails.purpose.trim()
                : '';
          }
        }

        visitResponse = await kioskApi.submitVisit(visitPayload, token);
      } else {
        // Walk-in visitor flow - CREATE new visit

        const now = formatLocalDateTime(new Date());

        visitPayload.visitorTypeId = selectedVisitorType
          ? parseInt(selectedVisitorType)
          : 1;
        visitPayload.status = 2; // Sign In
        visitPayload.signIn = now;
        visitPayload.allDay = 0;
        // NOTE: scheduleIn and scheduleOut are NOT set for walk-in visitors

        visitPayload.visitors = [
          {
            firstName: signInData.firstName,
            middleName: signInData.middleName,
            lastName: signInData.lastName,
            status: 2, // Sign In
            signIn: now,
          },
        ];

        // Add visit details from form
        if (visitDetails) {
          if (
            typeof visitDetails.email === 'string' &&
            visitPayload.visitors?.[0]
          ) {
            visitPayload.visitors[0].email = visitDetails.email.trim();
          }
          if (
            typeof visitDetails.phone === 'string' &&
            visitPayload.visitors?.[0]
          ) {
            visitPayload.visitors[0].mobilePhone = visitDetails.phone.trim();
          }
          if ('company' in visitDetails) {
            visitPayload.company =
              typeof visitDetails.company === 'string'
                ? visitDetails.company.trim()
                : '';
          }
          if ('host' in visitDetails) {
            visitPayload.hostId = visitDetails.host
              ? parseInt(visitDetails.host)
              : -1;
          }
          if ('purpose' in visitDetails) {
            visitPayload.purpose =
              typeof visitDetails.purpose === 'string'
                ? visitDetails.purpose.trim()
                : '';
          }
        }

        visitResponse = await kioskApi.submitVisit(visitPayload, token);

        // Extract visitId and visitorId from response
        // API returns { value: [ { id, visitors: [...] } ] }
        const responseArray = visitResponse?.value || visitResponse?.data || [];
        const responseData = responseArray[0] || visitResponse;

        visitId = responseData.id || responseData.visitId;
        visitorId =
          responseData.visitors?.[0]?.id ||
          responseData.visitorId ||
          responseData.visitor?.id;

        if (!visitId || !visitorId) {
          console.error(
            "❌ Failed to extract IDs. Response structure:",
            visitResponse,
          );
          throw new Error(
            "Failed to get visitId or visitorId from create visit response",
          );
        }
      }

      // Step 2: Update agreements (PATCH /kiosk/visit/{visitId}/agreement)
      if (agreedAgreements.length > 0) {
        for (const agreementId of agreedAgreements) {
          await kioskApi.updateVisitorAgreement(
            visitId,
            {
              documentId: parseInt(agreementId),
              visitorId: [visitorId],
              hasAgreed: 1,
              hasSigned: 1,
            },
            token,
          );
        }

        // Step 2.5: Generate and upload agreement PDF
        try {
          // Get agreed agreements with content
          const agreedAgreementsData = await Promise.all(
            agreedAgreements.map(async (agreementId) => {
              const agreement = agreements.find(
                (a: any) => a.id === agreementId,
              );
              if (!agreement) return null;

              // If content is available, use it; otherwise fetch it
              let content = agreement.content || "";
              if (!content) {
                try {
                  const docContent = await kioskApi.getDocumentContent(
                    parseInt(agreementId),
                    token,
                  );
                  content = docContent?.content || "";
                } catch (error) {
                  console.error(
                    `Failed to fetch content for agreement ${agreementId}:`,
                    error,
                  );
                }
              }

              return {
                name: agreement.name || "",
                content: content,
              };
            }),
          );

          // Filter out null values
          const validAgreements = agreedAgreementsData.filter(
            (a): a is { name: string; content: string } => a !== null,
          );

          if (validAgreements.length > 0) {
            // Generate PDF
            const firstName = signInData.firstName || "";
            const lastName = signInData.lastName || "";
            const visitorName = `${firstName} ${lastName}`.trim();

            const pdfBlob = generateAgreementPDF({
              agreements: validAgreements,
              visitorName: visitorName || t("selfSignIn.error.unknown"),
              agreedText: t("selfSignIn.agreements.agreedText"),
            });

            // Upload PDF
            await kioskApi.uploadVisitorAgreementFile(
              visitId,
              visitorId,
              pdfBlob,
              token,
            );
          }
        } catch (error: any) {
          console.error(
            "❌ Failed to generate or upload agreement PDF:",
            error,
          );
          // Don't throw error - continue with sign-in process
        }
      }

      // Step 3: Upload photo (POST /kiosk/visit/{visitId}/visitor/{visitorId}/photo)
      if (uploadedPhoto?.originFileObj) {
        await kioskApi.uploadVisitorPhoto(
          visitId,
          visitorId,
          uploadedPhoto.originFileObj as File,
          token,
        );
      }

      // Step 4: Create or update custom field data
      // Read customFieldsData from sessionStorage to ensure we have the latest data
      let customFieldsToSubmit = customFieldsData;
      const savedCustomFieldsData = sessionStorage.getItem(STORAGE_KEY);
      if (savedCustomFieldsData) {
        try {
          const parsedData = JSON.parse(savedCustomFieldsData);
          if (parsedData.customFieldsData && Array.isArray(parsedData.customFieldsData)) {
            customFieldsToSubmit = parsedData.customFieldsData;
          }
        } catch (error) {
          console.error("Error reading customFieldsData from sessionStorage:", error);
        }
      }

      if (customFieldsToSubmit.length > 0) {
        // Fetch existing visit data to determine if we need to CREATE or UPDATE
        let existingVisitData: any[] = [];
        try {
          existingVisitData = await kioskApi.getVisitData(visitId, token);
        } catch {
          // If fetch fails, assume no existing data
        }

        for (const customField of customFieldsToSubmit) {
          try {
            // Find existing data entry for this field
            const existingEntry = existingVisitData.find(
              (item: any) =>
                (item.formField?.id || item.formFieldId || item.fieldId) ===
                customField.fieldId,
            );

            if (existingEntry) {
              // UPDATE existing data
              await kioskApi.updateCustomFieldData(
                visitId,
                existingEntry.id,
                {
                  fieldId: customField.fieldId,
                  value:
                    typeof customField.value === 'string'
                      ? customField.value.trim()
                      : customField.value,
                },
                token,
              );
            } else {
              // CREATE new data
              await kioskApi.createVisitData(
                visitId,
                {
                  fieldId: customField.fieldId,
                  value:
                    typeof customField.value === 'string'
                      ? customField.value.trim()
                      : customField.value,
                },
                token,
              );
            }
          } catch (error: any) {
            console.error(
              `  ❌ Failed to save custom field ${customField.fieldId}:`,
              error,
            );
          }
        }
      }

      return { success: true };
    } catch (error: any) {
      console.error("❌ Error during sign-in sequence:", error);
      return {
        success: false,
        error: error.message || t("selfSignIn.error.failedSignIn"),
      };
    }
  }, [
    form,
    visitDetailsForm,
    selectedScheduledVisitor,
    selectedVisitorType,
    locationId,
    token,
    agreedAgreements,
    uploadedPhoto,
    customFieldsData,
    agreements,
    t,
  ]);

  // Auto-skip agreements page if no agreements (safety check)
  // This should not normally be reached since navigateAfterVisitDetails checks for agreements
  useEffect(() => {
    if (!currentStep || currentStep !== "agreements") {
      return;
    }
    // If we somehow reached agreements page without any agreements, skip it
    if (
      !isLoading &&
      (!agreements || !Array.isArray(agreements) || agreements.length === 0)
    ) {
      // Skip to photo capture
      if (workflowInConfig?.visitorPhoto === 1) {
        setCurrentStep("photoCapture");
      } else {
        // Photo capture also disabled - proceed to final submission
        setIsSubmitting(true);
        handleFinalSubmit().then((result) => {
          setIsSubmitting(false);
          if (result.success) {
            setCurrentStep("confirmation");
          } else {
            console.error("Sign-in failed:", result.error);
            Modal.error({
              title: t("selfSignIn.modal.signInFailed"),
              content:
                result.error ||
                t(
                  "selfSignIn.modal.signInFailedContent",
                  "Unable to complete sign in. Please try again or see an attendant.",
                ),
              okText: t("common.ok"),
              centered: true,
            });
          }
        });
      }
    }
  }, [
    currentStep,
    agreements,
    isLoading,
    workflowInConfig,
    t,
    handleFinalSubmit,
  ]);

  const isNextButtonDisabled = () => {
    if (isSubmitting || isPageLoading) {
      return true;
    }

    // For agreements page with required agreements
    if (
      currentStep === "agreements" &&
      workflowInConfig?.agreementsAccept === 1
    ) {
      return agreedAgreements.length < agreements.length;
    }

    // For visitor selection pages
    if (currentStep === "scheduledVisitorSelection") {
      return !selectedScheduledVisitor && !isNameNotOnList;
    }

    if (currentStep === "signedInVisitorSelection") {
      return !selectedSignedInVisitor;
    }

    return false;
  };

  // Helper function to check if visitDetails page should be shown
  // Skip if all fields are disabled (included === 0)
  // For walk-in visitors: show if any field is enabled
  // For scheduled visitors: skip if no required fields OR all required fields are pre-filled
  const shouldShowVisitDetails = () => {
    // First check if ANY field is enabled (included === 1)
    const hasAnyStandardField =
      workflowInFields &&
      (workflowInFields.email?.included === 1 ||
        workflowInFields.visitCompany?.included === 1 ||
        workflowInFields.visitPhone?.included === 1 ||
        workflowInFields.host?.included === 1 ||
        workflowInFields.visitPurpose?.included === 1);

    const hasAnyCustomField =
      workflowInFieldsCustom &&
      workflowInFieldsCustom.some((field: any) => field.included === 1);

    // If no fields are enabled at all, always skip visitDetails
    if (!hasAnyStandardField && !hasAnyCustomField) {
      return false;
    }

    // Walk-in visitor (not on list) - show visitDetails if any field is enabled
    if (!selectedScheduledVisitor) {
      return true;
    }

    // Scheduled visitor - check if there are required fields
    const hasRequiredStandardFields =
      workflowInFields &&
      ((workflowInFields.email?.included === 1 &&
        workflowInFields.email?.required === 1) ||
        (workflowInFields.visitCompany?.included === 1 &&
          workflowInFields.visitCompany?.required === 1) ||
        (workflowInFields.visitPhone?.included === 1 &&
          workflowInFields.visitPhone?.required === 1) ||
        (workflowInFields.host?.included === 1 &&
          workflowInFields.host?.required === 1) ||
        (workflowInFields.visitPurpose?.included === 1 &&
          workflowInFields.visitPurpose?.required === 1));

    const hasRequiredCustomFields =
      workflowInFieldsCustom &&
      workflowInFieldsCustom.some(
        (field: any) => field.included === 1 && field.required === 1,
      );

    // No required fields - skip visitDetails
    if (!hasRequiredStandardFields && !hasRequiredCustomFields) {
      return false;
    }

    // Has required fields - check if all are pre-filled from scheduled visitor data
    const visitor = selectedScheduledVisitor;
    let allRequiredFilled = true;

    // Check standard required fields
    if (
      workflowInFields?.email?.included === 1 &&
      workflowInFields?.email?.required === 1
    ) {
      if (!visitor.email) allRequiredFilled = false;
    }
    if (
      workflowInFields?.visitCompany?.included === 1 &&
      workflowInFields?.visitCompany?.required === 1
    ) {
      if (!visitor.company) allRequiredFilled = false;
    }
    if (
      workflowInFields?.visitPhone?.included === 1 &&
      workflowInFields?.visitPhone?.required === 1
    ) {
      if (!visitor.phone) allRequiredFilled = false;
    }
    if (
      workflowInFields?.host?.included === 1 &&
      workflowInFields?.host?.required === 1
    ) {
      if (!visitor.hostId) allRequiredFilled = false;
    }
    if (
      workflowInFields?.visitPurpose?.included === 1 &&
      workflowInFields?.visitPurpose?.required === 1
    ) {
      if (!visitor.purpose) allRequiredFilled = false;
    }

    // Check custom required fields
    if (hasRequiredCustomFields && workflowInFieldsCustom) {
      workflowInFieldsCustom.forEach((field: any) => {
        if (field.included === 1 && field.required === 1) {
          const customFieldValue =
            visitor.customFields?.[`customField_${field.id}`] ||
            visitor.customFields?.[field.id];
          if (!customFieldValue) allRequiredFilled = false;
        }
      });
    }

    // If all required fields are pre-filled, skip visitDetails
    return !allRequiredFilled;
  };

  // Legacy function name for backward compatibility
  const hasVisitDetailsFields = shouldShowVisitDetails;

  // Helper function to navigate to the appropriate step after visitDetails (or skip it)
  const navigateAfterVisitDetails = async () => {
    // Check if agreements page should be displayed
    // Only show if agreementsDisplay is enabled AND there are agreements to display
    const shouldShowAgreements =
      workflowInConfig?.agreementsDisplay === 1 &&
      agreements &&
      Array.isArray(agreements) &&
      agreements.length > 0;

    if (shouldShowAgreements) {
      setCurrentStep("agreements");
    } else {
      // Skip agreements page

      // Check if photo capture should be displayed
      if (workflowInConfig?.visitorPhoto === 1) {
        setCurrentStep("photoCapture");
      } else {
        setIsSubmitting(true);
        const result = await handleFinalSubmit();
        setIsSubmitting(false);

        if (result.success) {
          setCurrentStep("confirmation");
        } else {
          console.error("Sign-in failed:", result.error);
          Modal.error({
            title: t("selfSignIn.modal.signInFailed"),
            content:
              result.error ||
              t(
                "selfSignIn.modal.signInFailedContent",
                "Unable to complete sign in. Please try again or see an attendant.",
              ),
            okText: t("common.ok"),
            centered: true,
          });
        }
      }
    }
  };

  // Helper function to reset workflow configs to default
  const resetToDefaultConfig = () => {
    // Reset workflow configs to default
    if (defaultConfigsRef.current) {
      setWorkflowInConfig(defaultConfigsRef.current.workflowInConfig);
      setWorkflowOutConfig(defaultConfigsRef.current.workflowOutConfig);
      setWorkflowInFields(defaultConfigsRef.current.workflowInFields);
      setWorkflowInFieldsCustom(
        defaultConfigsRef.current.workflowInFieldsCustom,
      );
      setAgreements(defaultConfigsRef.current.agreements);
    }

    // Clear visitor type selection state
    setActiveVisitorTypeId(null);
    setSelectedVisitorType(null);
    setVisitorTypes([]);

    // Reset auto-skip flag
    setVisitDetailsAutoSkipped(null);
  };

  // Helper function to reset to selection page
  const resetToSelection = () => {
    // Clear localStorage
    sessionStorage.removeItem(STORAGE_KEY);

    // Reset all forms
    form.resetFields();
    signOutForm.resetFields();
    visitorTypeForm.resetFields();
    visitDetailsForm.resetFields();

    // Reset all state
    setSelectedVisitorType(null);
    setScheduledVisitors([]);
    setSelectedScheduledVisitor(null);
    setIsNameNotOnList(false);
    setSignedInVisitors([]);
    setSelectedSignedInVisitor(null);
    setAgreedAgreements([]);
    setUploadedPhoto(null);
    setCustomFieldsData([]);
    setActiveVisitorTypeId(null);
    setIsSignOutFlow(false);
    setSignInFirstName("");
    setSignInLastName("");
    setSignInMiddleName("");

    // Reset workflow configs to default
    if (defaultConfigsRef.current) {
      setWorkflowInConfig(defaultConfigsRef.current.workflowInConfig);
      setWorkflowOutConfig(defaultConfigsRef.current.workflowOutConfig);
      setWorkflowInFields(defaultConfigsRef.current.workflowInFields);
      setWorkflowInFieldsCustom(
        defaultConfigsRef.current.workflowInFieldsCustom,
      );
      setAgreements(defaultConfigsRef.current.agreements);
    }

    // Clear visitor types
    setVisitorTypes([]);

    // Reset auto-skip flag
    setVisitDetailsAutoSkipped(null);

    // Go to selection page
    setCurrentStep("selection");
  };

  const handlePrevious = () => {
    if (currentStep === "photoCapture") {
      // Check if agreements page was displayed
      const shouldShowAgreements =
        workflowInConfig?.agreementsDisplay === 1 &&
        agreements &&
        Array.isArray(agreements) &&
        agreements.length > 0;

      if (shouldShowAgreements) {
        setCurrentStep("agreements");
      } else if (hasVisitDetailsFields() && !visitDetailsAutoSkipped) {
        // Go back to visit details if it has fields AND it wasn't auto-skipped
        setCurrentStep("visitDetails");
      } else {
        // Visit details was skipped or auto-skipped, go back further
        if (selectedScheduledVisitor) {
          setCurrentStep("scheduledVisitorSelection");
        } else if (visitorTypes.length > 0) {
          setCurrentStep("visitorTypeSelection");
        } else {
          setCurrentStep("signIn");
        }
      }
    } else if (currentStep === "agreements") {
      // Go back to visit details if it has fields AND it wasn't auto-skipped
      if (hasVisitDetailsFields() && !visitDetailsAutoSkipped) {
        setCurrentStep("visitDetails");
      } else {
        // Visit details was skipped or auto-skipped, go back further
        if (selectedScheduledVisitor) {
          setCurrentStep("scheduledVisitorSelection");
        } else if (visitorTypes.length > 0) {
          setCurrentStep("visitorTypeSelection");
        } else {
          setCurrentStep("signIn");
        }
      }
    } else if (currentStep === "visitDetails") {
      // Go back based on workflow
      if (selectedScheduledVisitor) {
        // Scheduled visitor flow: go back to selection
        setCurrentStep("scheduledVisitorSelection");
      } else {
        // Walk-in visitor flow: go back to visitor type selection or sign in
        if (visitorTypes.length > 0) {
          setCurrentStep("visitorTypeSelection");
        } else {
          // Going back to signIn - reset to default config
          resetToDefaultConfig();
          setCurrentStep("signIn");
        }
      }
    } else if (currentStep === "visitorTypeSelection") {
      // Check if we came from scheduledVisitorSelection (walk-in flow)
      if (scheduledVisitors.length > 0) {
        // There are scheduled visitors, so go back to scheduledVisitorSelection
        setCurrentStep("scheduledVisitorSelection");
      } else {
        // No scheduled visitors, came from signIn directly - reset to default config
        resetToDefaultConfig();
        setCurrentStep("signIn");
      }
    } else if (currentStep === "scheduledVisitorSelection") {
      // Going back to signIn - reset to default config
      resetToDefaultConfig();
      setCurrentStep("signIn");
    } else if (currentStep === "signIn") {
      // Going back to selection - reset everything
      resetToSelection();
    } else if (currentStep === "redFlagDenied") {
      // Going back to signIn - reset to default config
      resetToDefaultConfig();
      setCurrentStep("signIn");
    } else if (currentStep === "signedInVisitorSelection") {
      // Going back to signOut - reset to default config
      resetToDefaultConfig();
      setCurrentStep("signOut");
    } else if (currentStep === "signOut") {
      // Going back to selection - reset everything
      resetToSelection();
    } else {
      // Default case - reset and go to selection
      resetToSelection();
    }
  };

  const handleNext = async () => {
    if (currentStep === "signIn") {
      try {
        await form.validateFields();
        const values = form.getFieldsValue();
        const normalizedValues = normalizeNameValues(values);

        // Check if name has changed - if so, reset all data
        const nameChanged =
          (signInFirstName && signInFirstName !== normalizedValues.firstName) ||
          (signInLastName && signInLastName !== normalizedValues.lastName);

        if (nameChanged) {
          // Name changed - reset all form data
          visitDetailsForm.resetFields();
          setCustomFieldsData([]);
          setAgreedAgreements([]);
          setUploadedPhoto(null);
          setScheduledVisitors([]);
          setSelectedScheduledVisitor(null);
          setIsNameNotOnList(false);
          setActiveVisitorTypeId(null);
          setVisitorTypes([]);

          // Clear from sessionStorage
          const existingStorageData = sessionStorage.getItem(STORAGE_KEY);
          if (existingStorageData) {
            try {
              const storageData = JSON.parse(existingStorageData);
              // Clear all form data
              if (storageData.forms) {
                delete storageData.forms.visitDetails;
                delete storageData.forms.visitorType;
              }
              // Clear other data
              storageData.agreedAgreements = [];
              storageData.customFieldsData = [];
              storageData.activeVisitorTypeId = null;
              storageData.scheduledVisitors = [];
              storageData.selectedScheduledVisitor = null;
              storageData.isNameNotOnList = false;
              sessionStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
            } catch (error) {
              console.error("Error clearing data from storage:", error);
            }
          }
        }

        // Save firstName, lastName, and middleName to state for later use
        setSignInFirstName(normalizedValues.firstName || "");
        setSignInLastName(normalizedValues.lastName || "");
        setSignInMiddleName(normalizedValues.middleName || "");

        // Save to localStorage
        saveFormValue("signIn", normalizedValues);

        // Show loading
        setIsSubmitting(true);

        // Call page handler to search for scheduled visitors
        const result = await handleSignInNext(normalizedValues, token);

        setIsSubmitting(false);

        if (result.type === "scheduledVisitors") {
          setScheduledVisitors(result.data || []);
          setCurrentStep("scheduledVisitorSelection");
        } else {
          // No scheduled visitor found (walk-in visitor)
          const walkInCheckResult = await handleScheduledVisitorSelectionNext(
            null,
            true,
            workflowByVisitorType,
            normalizedValues.firstName || "",
            normalizedValues.lastName || "",
            token,
            workflowInConfig,
            normalizedValues.middleName,
          );

          if (walkInCheckResult.type === "redFlag") {
            setCurrentStep("redFlagDenied");
          } else {
            const availableVisitorTypes = walkInCheckResult.visitorTypes || [];

            if (availableVisitorTypes.length > 0) {
              setVisitorTypes(availableVisitorTypes);
              setCurrentStep("visitorTypeSelection");
            } else {
              // No visitor types with workflowInConfig, use default workflow
              if (hasVisitDetailsFields()) {
                // Reset auto-skip flag before entering visitDetails
                setVisitDetailsAutoSkipped(null);
                setCurrentStep("visitDetails");
              } else {
                await navigateAfterVisitDetails();
              }
            }
          }
        }
      } catch (error) {
        setIsSubmitting(false);
        Modal.warning({
          title: t("selfSignIn.modal.notification"),
          content: t(
            "selfSignIn.modal.completeFields",
            "Please complete the required fields.",
          ),
          okText: t("common.confirm"),
          centered: true,
        });
      }
    } else if (currentStep === "scheduledVisitorSelection") {
      try {
        setIsSubmitting(true);

        const result = await handleScheduledVisitorSelectionNext(
          selectedScheduledVisitor,
          isNameNotOnList,
          workflowByVisitorType,
          signInFirstName,
          signInLastName,
          token,
          workflowInConfig,
          signInMiddleName,
        );

        setIsSubmitting(false);

        if (result.type === "redFlag") {
          setCurrentStep("redFlagDenied");
        } else if (result.type === "walkIn") {
          // Walk-in visitor (My name is not on the list)
          // Clear all previously entered data
          visitDetailsForm.resetFields();
          setCustomFieldsData([]);
          setAgreedAgreements([]);
          setUploadedPhoto(null);

          const availableVisitorTypes = result.visitorTypes || [];
          if (availableVisitorTypes.length > 0) {
            setVisitorTypes(availableVisitorTypes);
            setCurrentStep("visitorTypeSelection");
          } else {
            if (hasVisitDetailsFields()) {
              // Reset auto-skip flag before entering visitDetails
              setVisitDetailsAutoSkipped(null);
              setCurrentStep("visitDetails");
            } else {
              await navigateAfterVisitDetails();
            }
          }
        } else {
          // Scheduled visitor selected - apply visitor-type-specific config NOW

          // Clear all form data to allow fresh data from selected visitor
          visitDetailsForm.resetFields();
          setCustomFieldsData([]);
          setAgreedAgreements([]);
          setUploadedPhoto(null);
          const existingStorageData = sessionStorage.getItem(STORAGE_KEY);
          if (existingStorageData) {
            try {
              const storageData = JSON.parse(existingStorageData);
              if (storageData.forms) {
                delete storageData.forms.visitDetails;
              }
              sessionStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
            } catch (error) {
              console.error("Error clearing visitDetails from storage:", error);
            }
          }

          let appliedWorkflowInFields = workflowInFields; // Default
          let appliedWorkflowInFieldsCustom = workflowInFieldsCustom; // Default

          if (selectedScheduledVisitor && workflowByVisitorType.length > 0) {
            const visitorTypeConfig = workflowByVisitorType.find(
              (item: any) =>
                item.visitorType?.id ===
                  selectedScheduledVisitor.visitorTypeId ||
                item.visitorType?.name === selectedScheduledVisitor.visitorType,
            );
            if (visitorTypeConfig) {
              // Set active visitor type ID
              setActiveVisitorTypeId(
                visitorTypeConfig.visitorType?.id?.toString() || null,
              );

              // Apply all configs - if field is null/undefined in visitorTypeConfig, use null
              setWorkflowInConfig(visitorTypeConfig.workflowInConfig ?? null);
              setWorkflowOutConfig(visitorTypeConfig.workflowOutConfig ?? null);

              appliedWorkflowInFields = visitorTypeConfig.workflowField ?? null;
              setWorkflowInFields(appliedWorkflowInFields);

              // For localStorage, store null if undefined. For state, use empty array if null
              appliedWorkflowInFieldsCustom =
                visitorTypeConfig.customWorkFlowField ?? null;
              setWorkflowInFieldsCustom(appliedWorkflowInFieldsCustom || []);

              // Handle agreements - if null/undefined or empty array, set to empty array for state
              if (
                visitorTypeConfig.agreements &&
                Array.isArray(visitorTypeConfig.agreements) &&
                visitorTypeConfig.agreements.length > 0
              ) {
                const formattedAgreements = visitorTypeConfig.agreements
                  .filter((ag: any) => ag.document?.forSignIn === 1)
                  .map((agreement: any) => ({
                    id: agreement.document?.id?.toString() || "",
                    name: agreement.document?.name || "",
                    content: agreement.content || "",
                    type: agreement.document?.type,
                  }));
                setAgreements(formattedAgreements);
              } else {
                setAgreements([]);
              }
            } else {
              setActiveVisitorTypeId(null);
            }
          }

          // Fetch custom field data for the selected visit
          if (selectedScheduledVisitor?.visitId) {
            try {
              const visitData = await kioskApi.getVisitData(
                selectedScheduledVisitor.visitId,
                token,
              );

              // Update selectedScheduledVisitor with fetched custom field data
              const customFieldsData: any = {};
              if (visitData && Array.isArray(visitData)) {
                visitData.forEach((dataItem: any) => {
                  const fieldId = dataItem.formField?.id || dataItem.formFieldId || dataItem.fieldId;
                  if (
                    fieldId &&
                    dataItem.value !== null &&
                    dataItem.value !== undefined
                  ) {
                    customFieldsData[`customField_${fieldId}`] = dataItem.value;
                  }
                });
              }

              // Update selectedScheduledVisitor state with custom fields
              setSelectedScheduledVisitor({
                ...selectedScheduledVisitor,
                customFields: customFieldsData,
              });
            } catch (error) {
              console.error('Failed to fetch visit data:', error);
              // Continue without custom field data
            }
          }

          // Check if visitDetails should be shown for scheduled visitor
          // Skip if no required fields OR all required fields are pre-filled
          const hasRequiredStandardFields =
            appliedWorkflowInFields &&
            ((appliedWorkflowInFields.email?.included === 1 &&
              appliedWorkflowInFields.email?.required === 1) ||
              (appliedWorkflowInFields.visitCompany?.included === 1 &&
                appliedWorkflowInFields.visitCompany?.required === 1) ||
              (appliedWorkflowInFields.visitPhone?.included === 1 &&
                appliedWorkflowInFields.visitPhone?.required === 1) ||
              (appliedWorkflowInFields.host?.included === 1 &&
                appliedWorkflowInFields.host?.required === 1) ||
              (appliedWorkflowInFields.visitPurpose?.included === 1 &&
                appliedWorkflowInFields.visitPurpose?.required === 1));
          const hasRequiredCustomFields =
            appliedWorkflowInFieldsCustom &&
            appliedWorkflowInFieldsCustom.some(
              (field: any) => field.included === 1 && field.required === 1,
            );

          // Check if all required fields are pre-filled
          let allRequiredFilled = true;
          const visitor = selectedScheduledVisitor;

          if (hasRequiredStandardFields || hasRequiredCustomFields) {
            if (
              appliedWorkflowInFields?.email?.included === 1 &&
              appliedWorkflowInFields?.email?.required === 1
            ) {
              if (!visitor?.email) allRequiredFilled = false;
            }
            if (
              appliedWorkflowInFields?.visitCompany?.included === 1 &&
              appliedWorkflowInFields?.visitCompany?.required === 1
            ) {
              if (!visitor?.company) allRequiredFilled = false;
            }
            if (
              appliedWorkflowInFields?.visitPhone?.included === 1 &&
              appliedWorkflowInFields?.visitPhone?.required === 1
            ) {
              if (!visitor?.phone) allRequiredFilled = false;
            }
            if (
              appliedWorkflowInFields?.host?.included === 1 &&
              appliedWorkflowInFields?.host?.required === 1
            ) {
              if (!visitor?.hostId) allRequiredFilled = false;
            }
            if (
              appliedWorkflowInFields?.visitPurpose?.included === 1 &&
              appliedWorkflowInFields?.visitPurpose?.required === 1
            ) {
              if (!visitor?.purpose) allRequiredFilled = false;
            }

            // Check custom required fields
            if (hasRequiredCustomFields && appliedWorkflowInFieldsCustom) {
              appliedWorkflowInFieldsCustom.forEach((field: any) => {
                if (field.included === 1 && field.required === 1) {
                  const customFieldValue =
                    visitor?.customFields?.[`customField_${field.id}`] ||
                    visitor?.customFields?.[field.id];
                  if (!customFieldValue) allRequiredFilled = false;
                }
              });
            }
          }

          // Show visitDetails only if there are required fields that are NOT pre-filled
          const shouldShowVisitDetailsPage =
            (hasRequiredStandardFields || hasRequiredCustomFields) &&
            !allRequiredFilled;

          if (shouldShowVisitDetailsPage) {
            // Reset auto-skip flag before entering visitDetails
            setVisitDetailsAutoSkipped(null);
            setCurrentStep("visitDetails");
          } else {
            // Visit details page is being skipped - extract custom fields from scheduled visitor if available
            if (selectedScheduledVisitor?.customFields) {
              const customFieldsFromScheduled: any[] = [];
              Object.keys(selectedScheduledVisitor.customFields).forEach((key) => {
                const fieldId = key.replace("customField_", "");
                const value = selectedScheduledVisitor.customFields[key];
                if (value !== "" && value !== null && value !== undefined) {
                  customFieldsFromScheduled.push({
                    fieldId: parseInt(fieldId),
                    value: value,
                  });
                }
              });

              if (customFieldsFromScheduled.length > 0) {
                setCustomFieldsData(customFieldsFromScheduled);

                // Save to sessionStorage
                const savedData = sessionStorage.getItem(STORAGE_KEY);
                let allData: any = {};
                if (savedData) {
                  try {
                    allData = JSON.parse(savedData);
                  } catch (error) {
                    console.error("Error parsing saved data:", error);
                  }
                }
                allData.customFieldsData = customFieldsFromScheduled;
                sessionStorage.setItem(STORAGE_KEY, JSON.stringify(allData));
              }
            }

            await navigateAfterVisitDetails();
          }
        }
      } catch (error) {
        setIsSubmitting(false);
        Modal.error({
          title: t("selfSignIn.modal.error"),
          content: t(
            "selfSignIn.modal.errorProcessing",
            "An error occurred while processing your selection. Please try again.",
          ),
          okText: t("common.ok"),
          centered: true,
        });
      }
    } else if (currentStep === "agreements") {
      try {
        setIsSubmitting(true);

        await handleAgreementsNext(
          agreedAgreements,
          agreements.length,
          workflowInConfig?.agreementsAccept,
        );

        setIsSubmitting(false);

        // Check if photo capture should be displayed
        if (workflowInConfig?.visitorPhoto === 1) {
          setCurrentStep("photoCapture");
        } else {
          setIsSubmitting(true);
          const result = await handleFinalSubmit();
          setIsSubmitting(false);

          if (result.success) {
            setCurrentStep("confirmation");
          } else {
            console.error("Sign-in failed:", result.error);
            Modal.error({
              title: t("selfSignIn.modal.signInFailed"),
              content:
                result.error ||
                t(
                  "selfSignIn.modal.signInFailedContent",
                  "Unable to complete sign in. Please try again or see an attendant.",
                ),
              okText: t("common.ok"),
              centered: true,
            });
          }
        }
      } catch (error) {
        setIsSubmitting(false);
        Modal.error({
          title: t("selfSignIn.modal.error"),
          content: t(
            "selfSignIn.modal.errorAgreements",
            "An error occurred while processing agreements. Please try again.",
          ),
          okText: t("common.ok"),
          centered: true,
        });
      }
    } else if (currentStep === "photoCapture") {
      // Photo is optional, so we don't require it
      // Just proceed to final submit
      setIsSubmitting(true);
      const result = await handleFinalSubmit();
      setIsSubmitting(false);

      if (result.success) {
        setCurrentStep("confirmation");
      } else {
        console.error("Sign-in failed:", result.error);
        Modal.error({
          title: t("selfSignIn.modal.signInFailed"),
          content:
            result.error ||
            t(
              "selfSignIn.modal.signInFailedContent",
              "Unable to complete sign in. Please try again or see an attendant.",
            ),
          okText: t("common.ok"),
          centered: true,
        });
      }
    } else if (currentStep === "visitorTypeSelection") {
      try {
        // Validate first before showing loading
        const result = await handleVisitorTypeSelectionNext(
          visitorTypeForm,
          workflowByVisitorType,
          signInFirstName,
          signInLastName,
          token,
        );

        // Check if red flag was detected
        if (result.type === "redFlag") {
          setCurrentStep("redFlagDenied");
          return;
        }

        // Validation successful - now show loading
        setIsSubmitting(true);

        setSelectedVisitorType(result.visitorTypeId || null);

        // Save to localStorage
        saveFormValue("visitorType", { visitorType: result.visitorTypeId });

        // Clear all previously entered data except firstName/middleName/lastName
        visitDetailsForm.resetFields();
        setAgreedAgreements([]);
        setUploadedPhoto(null);
        setCustomFieldsData([]);

        // Clear visitDetails and agreements from sessionStorage (keep signIn form)
        const existingStorageData = sessionStorage.getItem(STORAGE_KEY);
        if (existingStorageData) {
          try {
            const storageData = JSON.parse(existingStorageData);
            if (storageData.forms) {
              delete storageData.forms.visitDetails;
              // Don't delete forms.visitorType - we just saved it above
            }
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
          } catch (error) {
            console.error("Error clearing form data:", error);
          }
        }

        // Apply visitor-type-specific config NOW (when Next is clicked)

        let appliedWorkflowInFields = workflowInFields; // Default
        let appliedWorkflowInFieldsCustom = workflowInFieldsCustom; // Default
        let appliedWorkflowInConfig = workflowInConfig;
        let appliedWorkflowOutConfig = workflowOutConfig;
        let appliedAgreements = agreements;
        let appliedActiveVisitorTypeId: string | null = null;

        if (result.visitorTypeId && workflowByVisitorType.length > 0) {
          const visitorTypeConfig = workflowByVisitorType.find(
            (item: any) =>
              item.visitorType?.id?.toString() === result.visitorTypeId,
          );

          if (visitorTypeConfig) {
            // Set active visitor type ID
            appliedActiveVisitorTypeId =
              visitorTypeConfig.visitorType?.id?.toString() || null;
            setActiveVisitorTypeId(appliedActiveVisitorTypeId);

            // Apply all configs - if field is null/undefined in visitorTypeConfig, use null
            appliedWorkflowInConfig =
              visitorTypeConfig.workflowInConfig ?? null;
            setWorkflowInConfig(appliedWorkflowInConfig);

            appliedWorkflowOutConfig =
              visitorTypeConfig.workflowOutConfig ?? null;
            setWorkflowOutConfig(appliedWorkflowOutConfig);

            appliedWorkflowInFields = visitorTypeConfig.workflowField ?? null;
            setWorkflowInFields(appliedWorkflowInFields);

            // For localStorage, store null if undefined. For state, use empty array if null
            appliedWorkflowInFieldsCustom =
              visitorTypeConfig.customWorkFlowField ?? null;
            setWorkflowInFieldsCustom(appliedWorkflowInFieldsCustom || []);

            // Handle agreements - if null/undefined or empty array, set to empty array
            if (
              visitorTypeConfig.agreements &&
              Array.isArray(visitorTypeConfig.agreements) &&
              visitorTypeConfig.agreements.length > 0
            ) {
              const formattedAgreements = visitorTypeConfig.agreements
                .filter((ag: any) => ag.document?.forSignIn === 1)
                .map((agreement: any) => ({
                  id: agreement.document?.id?.toString() || "",
                  name: agreement.document?.name || "",
                  content: agreement.content || "",
                  type: agreement.document?.type,
                }));
              appliedAgreements = formattedAgreements;
              setAgreements(formattedAgreements);
            } else {
              // visitorTypeConfig.agreements is null/undefined - use empty array
              appliedAgreements = [];
              setAgreements([]);
            }
          } else {
            setActiveVisitorTypeId(null);
          }
        }

        // Manually save workflow configs to localStorage after applying them
        const existingData = sessionStorage.getItem(STORAGE_KEY);
        let storageData: any = {};
        if (existingData) {
          try {
            storageData = JSON.parse(existingData);
          } catch (error) {
            console.error("Error parsing localStorage:", error);
          }
        }

        // Update localStorage with the applied workflow configs (not state values)
        // Convert empty arrays to null for localStorage
        storageData.activeVisitorTypeId = appliedActiveVisitorTypeId;
        storageData.workflowInConfig = appliedWorkflowInConfig;
        storageData.workflowOutConfig = appliedWorkflowOutConfig;
        storageData.workflowInFields = appliedWorkflowInFields;
        storageData.workflowInFieldsCustom =
          appliedWorkflowInFieldsCustom &&
          appliedWorkflowInFieldsCustom.length > 0
            ? appliedWorkflowInFieldsCustom
            : null;
        storageData.agreements =
          appliedAgreements && appliedAgreements.length > 0
            ? appliedAgreements
            : null;

        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));

        setIsSubmitting(false);

        // Check if there are visible fields (using applied config)
        const hasStandardFields =
          appliedWorkflowInFields &&
          (appliedWorkflowInFields.email?.included === 1 ||
            appliedWorkflowInFields.visitCompany?.included === 1 ||
            appliedWorkflowInFields.visitPhone?.included === 1 ||
            appliedWorkflowInFields.host?.included === 1 ||
            appliedWorkflowInFields.visitPurpose?.included === 1);
        const hasCustomFields =
          appliedWorkflowInFieldsCustom &&
          appliedWorkflowInFieldsCustom.some(
            (field: any) => field.included === 1,
          );

        if (hasStandardFields || hasCustomFields) {
          // Reset auto-skip flag before entering visitDetails
          setVisitDetailsAutoSkipped(null);
          setCurrentStep("visitDetails");
        } else {
          await navigateAfterVisitDetails();
        }
      } catch (error) {
        setIsSubmitting(false);
        Modal.warning({
          title: t("selfSignIn.modal.notification"),
          content: t(
            "selfSignIn.modal.completeFields",
            "Please complete the required fields.",
          ),
          okText: t("common.confirm"),
          centered: true,
        });
      }
    } else if (currentStep === "visitDetails") {
      try {
        // User manually clicked Next, so this is NOT an auto-skip
        setVisitDetailsAutoSkipped(false);

        // Validate form fields first (same as signIn page)
        await visitDetailsForm.validateFields();

        setIsSubmitting(true);

        const result = await handleVisitDetailsNext(visitDetailsForm);

        // Save to localStorage
        saveFormValue("visitDetails", result.values);

        // Store custom fields data in both state AND sessionStorage
        const customFields = result.customFieldsData || [];
        setCustomFieldsData(customFields);

        // Save customFieldsData to sessionStorage for reliable access in handleFinalSubmit
        const savedData = sessionStorage.getItem(STORAGE_KEY);
        let allData: any = {};
        if (savedData) {
          try {
            allData = JSON.parse(savedData);
          } catch (error) {
            console.error("Error parsing saved data:", error);
          }
        }
        allData.customFieldsData = customFields;
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(allData));

        setIsSubmitting(false);

        // Navigate to next step after visit details
        await navigateAfterVisitDetails();
      } catch (error) {
        setIsSubmitting(false);
        Modal.warning({
          title: t("selfSignIn.modal.notification"),
          content: t(
            "selfSignIn.modal.completeFields",
            "Please complete the required fields.",
          ),
          okText: t("common.confirm"),
          centered: true,
        });
      }
    } else if (currentStep === "signOut") {
      try {
        await signOutForm.validateFields();
        const values = signOutForm.getFieldsValue();
        const normalizedValues = normalizeNameValues(values);

        // Check if name has changed - if so, reset all data
        const savedData = sessionStorage.getItem(STORAGE_KEY);
        let previousFirstName = "";
        let previousLastName = "";
        if (savedData) {
          try {
            const data = JSON.parse(savedData);
            previousFirstName = data.forms?.signOut?.firstName || "";
            previousLastName = data.forms?.signOut?.lastName || "";
          } catch (error) {
            // Ignore
          }
        }

        const nameChanged =
          (previousFirstName &&
            previousFirstName !== normalizedValues.firstName) ||
          (previousLastName && previousLastName !== normalizedValues.lastName);

        if (nameChanged) {
          // Name changed - reset signed-in visitors
          setSignedInVisitors([]);
          setSelectedSignedInVisitor(null);

          // Clear from sessionStorage
          const existingStorageData = sessionStorage.getItem(STORAGE_KEY);
          if (existingStorageData) {
            try {
              const storageData = JSON.parse(existingStorageData);
              storageData.signedInVisitors = [];
              storageData.selectedSignedInVisitor = null;
              sessionStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
            } catch (error) {
              console.error("Error clearing data from storage:", error);
            }
          }
        }

        // Save to localStorage
        saveFormValue("signOut", normalizedValues);

        setIsSubmitting(true);

        // Call page handler to search for signed-in visitors
        const result = await handleSignOutNext(
          normalizedValues,
          token,
          locationId,
        );

        setIsSubmitting(false);

        // Check if no visitors found
        if (result.signedInVisitors.length === 0) {
          Modal.warning({
            title: t("selfSignIn.modal.notification"),
            content: t(
              "selfSignIn.modal.nameNotFound",
              "Name not found. Please try again.",
            ),
            okText: t("common.confirm"),
            centered: true,
            onOk: () => {
              // Stay on sign out page, user can try again
            },
          });
        } else {
          setSignedInVisitors(result.signedInVisitors);
          setCurrentStep("signedInVisitorSelection");
        }
      } catch (error) {
        setIsSubmitting(false);
        Modal.warning({
          title: t("selfSignIn.modal.notification"),
          content: t(
            "selfSignIn.modal.completeFields",
            "Please complete the required fields.",
          ),
          okText: t("common.confirm"),
          centered: true,
        });
      }
    } else if (currentStep === "signedInVisitorSelection") {
      try {
        setIsSubmitting(true);

        await handleSignedInVisitorSelectionNext(selectedSignedInVisitor);

        // Find visitor-type-specific config and apply NOW
        let appliedWorkflowOutConfig = workflowOutConfig; // Default

        if (selectedSignedInVisitor && workflowByVisitorType.length > 0) {
          const visitorTypeConfig = workflowByVisitorType.find(
            (item: any) =>
              item.visitorType?.id === selectedSignedInVisitor.visitorTypeId ||
              item.visitorType?.name === selectedSignedInVisitor.visitorType,
          );

          if (visitorTypeConfig) {
            // Set active visitor type ID
            setActiveVisitorTypeId(
              visitorTypeConfig.visitorType?.id?.toString() || null,
            );

            // Apply all configs - if field is null/undefined in visitorTypeConfig, use null
            setWorkflowInConfig(visitorTypeConfig.workflowInConfig ?? null);

            appliedWorkflowOutConfig =
              visitorTypeConfig.workflowOutConfig ?? null;
            setWorkflowOutConfig(appliedWorkflowOutConfig);

            setWorkflowInFields(visitorTypeConfig.workflowField ?? null);

            // For state, use empty array if null
            setWorkflowInFieldsCustom(
              visitorTypeConfig.customWorkFlowField ?? [],
            );

            // Handle agreements for sign out
            if (
              visitorTypeConfig.agreements &&
              Array.isArray(visitorTypeConfig.agreements) &&
              visitorTypeConfig.agreements.length > 0
            ) {
              const formattedAgreements = visitorTypeConfig.agreements
                .filter((ag: any) => ag.document?.forSignOut === 1)
                .map((agreement: any) => ({
                  id: agreement.document?.id?.toString() || "",
                  name: agreement.document?.name || "",
                  content: agreement.content || "",
                  type: agreement.document?.type,
                }));
              setAgreements(formattedAgreements);
            } else {
              setAgreements([]);
            }
          } else {
            setActiveVisitorTypeId(null);
          }
        }

        setIsSubmitting(false);

        // Show sign out confirmation modal before proceeding
        Modal.confirm({
          title: t("selfSignIn.modal.signOutConfirmTitle"),
          content: t("selfSignIn.modal.signOutConfirmContent"),
          okText: t("common.confirm"),
          cancelText: t("common.cancel"),
          centered: true,
          onOk: async () => {
            // Submit sign-out
            setIsSubmitting(true);

            try {
              // Helper function to format date to local timezone in YYYY-MM-DD HH:MM:SS format
              const formatLocalDateTime = (date: Date): string => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                const hours = String(date.getHours()).padStart(2, "0");
                const minutes = String(date.getMinutes()).padStart(2, "0");
                const seconds = String(date.getSeconds()).padStart(2, "0");
                return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
              };

              const now = formatLocalDateTime(new Date());

              // Sign-out payload
              // For sign-out, exclude firstName/middleName/lastName - visitor already exists
              const visitPayload = {
                visitId:
                  selectedSignedInVisitor.visitId || selectedSignedInVisitor.id,
                status: 3, // Sign Out
                signOut: now,
                visitors: [
                  {
                    visitorId:
                      selectedSignedInVisitor.visitorId ||
                      selectedSignedInVisitor.visitor?.id ||
                      selectedSignedInVisitor.id,
                    status: 3, // Sign Out
                    signOut: now,
                  },
                ],
              };

              // Call submit visit API (UPDATE existing visit for sign-out)
              await kioskApi.submitVisit(visitPayload, token);

              setIsSubmitting(false);
              setCurrentStep("confirmation");
            } catch (error: any) {
              console.error("❌ Error during sign-out:", error);
              setIsSubmitting(false);
              Modal.error({
                title: t("selfSignIn.modal.signOutFailed"),
                content:
                  error.message || t("selfSignIn.modal.signOutFailedContent"),
                okText: t("common.ok"),
                centered: true,
              });
            }
          },
        });
      } catch (error) {
        setIsSubmitting(false);
        Modal.error({
          title: t("selfSignIn.modal.error"),
          content: t(
            "selfSignIn.modal.errorProcessing",
            "An error occurred while processing your selection. Please try again.",
          ),
          okText: t("common.ok"),
          centered: true,
        });
      }
    }
  };

  const handleStartOver = () => {
    const resetAll = async () => {
      // Clear localStorage first
      sessionStorage.removeItem(STORAGE_KEY);

      // Reset all forms
      form.resetFields();
      signOutForm.resetFields();
      visitorTypeForm.resetFields();
      visitDetailsForm.resetFields();

      // Reset all state (except currentStep - will be set after API fetch)
      setSelectedVisitorType(null);
      setScheduledVisitors([]);
      setSelectedScheduledVisitor(null);
      setIsNameNotOnList(false);
      setSignedInVisitors([]);
      setSelectedSignedInVisitor(null);
      setAgreedAgreements([]);
      setUploadedPhoto(null);
      setCustomFieldsData([]);
      setActiveVisitorTypeId(null);
      setSignInFirstName("");
      setSignInLastName("");
      setSignInMiddleName("");

      // Immediately move away from confirmation page to prevent "Sign In Successful" flash
      // Temporary step until API fetch completes
      setCurrentStep("selection");
      setIsSignOutFlow(false);
      window.history.replaceState({}, "", `/selfSignIn?${token}`);

      // Show loading
      setIsPageLoading(true);

      try {
        // Re-fetch all configuration from APIs to get latest settings
        const [
          workflowInConfigData,
          workflowInFieldsData,
          workflowInFieldsCustomData,
          agreementsData,
          workflowByVisitorTypeData,
        ] = await Promise.all([
          kioskApi.getWorkflowInConfig(token),
          kioskApi.getWorkflowInFields(token),
          kioskApi.getWorkflowInFieldsCustom(token),
          kioskApi.getLocationAgreements(token),
          kioskApi.getWorkflowByVisitorType(token),
        ]);

        // Store workflowByVisitorType
        setWorkflowByVisitorType(workflowByVisitorTypeData);

        // Update default configs for later reset
        const workflowIn = workflowInConfigData?.workflowIn?.[0];
        const workflowOut = workflowInConfigData?.workflowOut?.[0];
        const defaultFormattedAgreements = agreementsData
          .filter((ag: any) => ag.forSignIn === 1)
          .map((agreement: any) => ({
            id: agreement.id?.toString() || "",
            name: agreement.name || "",
          }));

        defaultConfigsRef.current = {
          workflowInConfig: workflowIn || workflowInConfigData,
          workflowOutConfig: workflowOut || null,
          workflowInFields: workflowInFieldsData,
          workflowInFieldsCustom: workflowInFieldsCustomData,
          agreements: defaultFormattedAgreements,
        };

        // Apply default workflow configs
        setWorkflowInConfig(workflowIn || workflowInConfigData);
        setWorkflowOutConfig(workflowOut || null);
        setWorkflowInFields(workflowInFieldsData);
        setWorkflowInFieldsCustom(workflowInFieldsCustomData);
        setAgreements(defaultFormattedAgreements);

        // Update self sign in/out flags
        setAllowSelfSignIn(workflowInConfigData?.selfSignIn ?? 1);
        setAllowSelfSignOut(workflowInConfigData?.selfSignOut ?? 1);

        // Update appearance settings
        if (workflowInConfigData?.appearance) {
          const { logoUrl: apiLogoUrl, backgroundUrl: apiBackgroundUrl } =
            workflowInConfigData.appearance;

          if (apiLogoUrl) setLogoUrl(apiLogoUrl);
          if (apiBackgroundUrl) setBackgroundUrl(apiBackgroundUrl);
        }

        // Clear visitor types
        setVisitorTypes([]);

        // Determine initial step based on latest configuration
        const signInEnabled = (workflowInConfigData?.selfSignIn ?? 1) === 1;
        const signOutEnabled = (workflowInConfigData?.selfSignOut ?? 1) === 1;

        if (!signInEnabled && !signOutEnabled) {
          // Both disabled - show disabled message
          setCurrentStep("disabled");
          window.history.replaceState(
            {},
            "",
            `/selfSignIn?${token}&step=disabled`,
          );
        } else {
          // Always show selection page regardless of enabled options
          setCurrentStep("selection");
          window.history.replaceState({}, "", `/selfSignIn?${token}`);
        }
      } catch (error) {
        console.error("Error refreshing configuration:", error);
        Modal.error({
          title: t("selfSignIn.modal.error"),
          content: t(
            "selfSignIn.modal.refreshFailed",
            "Failed to refresh configuration. Please try again.",
          ),
          okText: t("common.ok"),
          centered: true,
        });

        // Fallback to default configs if API fails
        if (defaultConfigsRef.current) {
          setWorkflowInConfig(defaultConfigsRef.current.workflowInConfig);
          setWorkflowOutConfig(defaultConfigsRef.current.workflowOutConfig);
          setWorkflowInFields(defaultConfigsRef.current.workflowInFields);
          setWorkflowInFieldsCustom(
            defaultConfigsRef.current.workflowInFieldsCustom,
          );
          setAgreements(defaultConfigsRef.current.agreements);
        }

        // Determine initial step based on current configuration
        const signInEnabled = allowSelfSignIn === 1;
        const signOutEnabled = allowSelfSignOut === 1;

        if (!signInEnabled && !signOutEnabled) {
          // Both disabled - show disabled message
          setCurrentStep("disabled");
          window.history.replaceState(
            {},
            "",
            `/selfSignIn?${token}&step=disabled`,
          );
        } else {
          // Always show selection page regardless of enabled options
          setCurrentStep("selection");
          window.history.replaceState({}, "", `/selfSignIn?${token}`);
        }
      } finally {
        setIsPageLoading(false);
      }
    };

    // If on confirmation or redFlagDenied page, don't show modal - just reset
    if (
      currentStep === "confirmation" ||
      currentStep === "redFlagDenied"
    ) {
      resetAll();
    } else {
      // Show confirmation modal for all other pages
      Modal.confirm({
        title: t("selfSignIn.modal.notification"),
        content: t(
          "selfSignIn.modal.startOverConfirm",
          "Return to the beginning and start over?",
        ),
        okText: t("common.ok"),
        cancelText: t("common.cancel"),
        centered: true,
        onOk: resetAll,
      });
    }
  };

  const handleAgreementChange = (agreementId: string, checked: boolean) => {
    if (checked) {
      setAgreedAgreements([...agreedAgreements, agreementId]);
    } else {
      setAgreedAgreements(agreedAgreements.filter((id) => id !== agreementId));
    }
  };

  const handlePhotoChange = (file: UploadFile | null) => {
    setUploadedPhoto(file);
  };

  const handleSelectScheduledVisitor = (visitor: any) => {
    setSelectedScheduledVisitor(visitor);
    setIsNameNotOnList(false); // Clear "not on list" selection

    // Clear ALL form data when switching to a different visitor
    visitDetailsForm.resetFields();
    setCustomFieldsData([]);
    setAgreedAgreements([]);
    setUploadedPhoto(null);

    // Clear ALL forms from sessionStorage
    const existingStorageData = sessionStorage.getItem(STORAGE_KEY);
    if (existingStorageData) {
      try {
        const storageData = JSON.parse(existingStorageData);
        // Clear all form data
        if (storageData.forms) {
          delete storageData.forms.visitDetails;
          delete storageData.forms.visitorType;
        }
        // Clear other data
        storageData.agreedAgreements = [];
        storageData.customFieldsData = [];
        // Don't clear activeVisitorTypeId here - will be set when Next is clicked
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
      } catch (error) {
        console.error("Error clearing form data from storage:", error);
      }
    }

    // Note: Config will be applied when Next button is clicked
  };

  const handleSelectSignedInVisitor = (visitor: any) => {
    setSelectedSignedInVisitor(visitor);
    // Note: Config will be applied when Next button is clicked
  };

  const handleNameNotOnList = () => {
    // Mark as "not on list" and clear selected visitor
    setIsNameNotOnList(true);
    setSelectedScheduledVisitor(null);
    // Clear active visitor type ID when switching to walk-in flow
    setActiveVisitorTypeId(null);

    // Clear ALL form data since this is now a walk-in visitor
    visitDetailsForm.resetFields();
    setCustomFieldsData([]);
    setAgreedAgreements([]);
    setUploadedPhoto(null);

    // Clear ALL forms from sessionStorage (visitDetails, visitorType, agreements, photo)
    const existingStorageData = sessionStorage.getItem(STORAGE_KEY);
    if (existingStorageData) {
      try {
        const storageData = JSON.parse(existingStorageData);
        // Clear all form data
        if (storageData.forms) {
          delete storageData.forms.visitDetails;
          delete storageData.forms.visitorType;
        }
        // Clear other data
        storageData.agreedAgreements = [];
        storageData.customFieldsData = [];
        storageData.activeVisitorTypeId = null;
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
      } catch (error) {
        console.error("Error clearing data from storage:", error);
      }
    }

    // Don't navigate yet - wait for Next button
  };

  const renderCurrentPage = () => {
    // During initial load, show nothing
    if (!currentStep) {
      return null;
    }

    // Show token error message if token is invalid
    if (tokenError) {
      return (
        <div
          className="workflow-container"
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
            padding: "40px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: "48px",
              marginBottom: "20px",
            }}
          >
            ⚠️
          </div>
          <div
            style={{
              fontSize: "20px",
              fontWeight: "bold",
              marginBottom: "16px",
              color: "#ff4d4f",
            }}
          >
            {t("selfSignIn.error.invalidTokenTitle")}
          </div>
          <div
            style={{
              fontSize: "16px",
              color: "#666",
              maxWidth: "400px",
            }}
          >
            {tokenError}
          </div>
        </div>
      );
    }

    // Show loading spinner while submitting (but not for page loading - let page handle it)
    if (isSubmitting) {
      return (
        <div
          className="workflow-container"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <Loader />
        </div>
      );
    }

    switch (currentStep) {
      case "selection":
        return (
          <SelectionPage
            onSignIn={handleSignIn}
            onSignOut={handleSignOut}
            allowSelfSignIn={allowSelfSignIn}
            allowSelfSignOut={allowSelfSignOut}
            onLanguageChange={handleLanguageChange}
            currentLanguage={selectedLanguage}
          />
        );
      case "signIn":
        return (
          <SignInPage
            form={form}
            welcomeMessage={t("selfSignIn.default.welcomeText")}
            workflowInFields={workflowInFields}
          />
        );
      case "scheduledVisitorSelection":
        return (
          <ScheduledVisitorSelectionPage
            scheduledVisitors={scheduledVisitors}
            selectedVisitor={selectedScheduledVisitor}
            isNameNotOnList={isNameNotOnList}
            onSelectVisitor={handleSelectScheduledVisitor}
            onNameNotOnList={handleNameNotOnList}
          />
        );
      case "visitorTypeSelection":
        return (
          <VisitorTypeSelectionPage
            form={visitorTypeForm}
            visitorTypes={visitorTypes}
          />
        );
      case "agreements":
        // If no agreements, show loading while auto-skipping
        if (agreements.length === 0) {
          return (
            <div
              className="workflow-container"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px",
              }}
            >
              <Loader />
            </div>
          );
        }
        return (
          <AgreementsPage
            agreements={agreements}
            agreedAgreements={agreedAgreements}
            onAgreementChange={handleAgreementChange}
            token={token}
            locationId={locationId}
            agreementsAccept={workflowInConfig?.agreementsAccept}
          />
        );
      case "photoCapture":
        return (
          <PhotoCapturePage
            uploadedPhoto={uploadedPhoto}
            onPhotoChange={handlePhotoChange}
          />
        );
      case "visitDetails":
        return (
          <>
            {/* Show loader overlay while checking if auto-skip is possible */}
            {visitDetailsAutoSkipped === null && selectedScheduledVisitor && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "white",
                  zIndex: 1000,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Loader />
              </div>
            )}
            <VisitDetailsPage
              form={visitDetailsForm}
              token={token}
              locationId={locationId}
              workflowInFields={workflowInFields}
              workflowInFieldsCustom={workflowInFieldsCustom}
              selectedScheduledVisitor={selectedScheduledVisitor}
              hosts={hosts}
              onLoadingChange={setIsPageLoading}
            />
          </>
        );
      case "signOut":
        return (
          <SignOutPage
            form={signOutForm}
            welcomeMessage={t("selfSignIn.signOut.welcomeMessage")}
            workflowInFields={workflowInFields}
          />
        );
      case "signedInVisitorSelection":
        return (
          <SignedInVisitorSelectionPage
            signedInVisitors={signedInVisitors}
            selectedVisitor={selectedSignedInVisitor}
            onSelectVisitor={handleSelectSignedInVisitor}
          />
        );
      case "redFlagDenied":
        return <RedFlagDeniedPage />;
      case "confirmation":
        return (
          <ConfirmationPage
            isSignOut={isSignOutFlow}
            completedText={
              isSignOutFlow
                ? t("selfSignIn.default.signOutCompletedText")
                : t("selfSignIn.default.completedText")
            }
          />
        );
      case "disabled":
        return <DisabledPage />;
      default:
        return null;
    }
  };

  const showNavigationButtons = () => {
    if (!currentStep) return false;

    // Show navigation buttons for most steps
    if (
      [
        "signIn",
        "scheduledVisitorSelection",
        "agreements",
        "photoCapture",
        "visitorTypeSelection",
        "visitDetails",
        "signOut",
      ].includes(currentStep)
    ) {
      return true;
    }
    // For signedInVisitorSelection, only show if there are visitors
    if (
      currentStep === "signedInVisitorSelection" &&
      signedInVisitors.length > 0
    ) {
      return true;
    }
    return false;
  };

  const showNoMatchesButton = () => {
    return (
      currentStep === "signedInVisitorSelection" &&
      signedInVisitors.length === 0
    );
  };

  const showRedFlagDeniedButton = () => {
    return currentStep === "redFlagDenied";
  };

  const showConfirmationButtons = () => {
    return currentStep === "confirmation";
  };

  return (
    <Layout className="self-signin-layout">
      <Content
        className="self-signin-content"
        style={
          backgroundUrl
            ? {
                backgroundImage: `url(${backgroundUrl})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }
            : {}
        }
      >
        {isLoading ? (
          <div className="loading-container">
            <Loader />
          </div>
        ) : (
          <>
            {/* Scrollable area with Logo and Content */}
            <div className="self-signin-scrollable">
              {/* Header with Logo */}
              <div className="self-signin-header">
                {logoUrl && <img src={logoUrl} alt="Logo" className="logo" />}
              </div>

              {/* Main Content Area */}
              <div className="self-signin-main">{renderCurrentPage()}</div>
            </div>

            {/* Footer with Navigation Buttons */}
            {showNavigationButtons() && (
              <div className="self-signin-footer">
                <OpButton
                  type="default"
                  size="large"
                  onClick={handlePrevious}
                  disabled={isSubmitting || isPageLoading}
                >
                  {t("selfSignIn.footer.previous")}
                </OpButton>
                <OpButton
                  type="default"
                  size="large"
                  onClick={handleStartOver}
                  disabled={isSubmitting || isPageLoading}
                >
                  {t("selfSignIn.footer.startOver")}
                </OpButton>
                <OpButton
                  type="primary"
                  size="large"
                  onClick={handleNext}
                  disabled={isNextButtonDisabled()}
                >
                  {t("selfSignIn.footer.next")}
                </OpButton>
              </div>
            )}

            {/* Red Flag Denied - Only Start Over button */}
            {showRedFlagDeniedButton() && (
              <div className="self-signin-footer centered">
                <OpButton
                  type="default"
                  size="large"
                  onClick={handleStartOver}
                  className="start-over-only"
                  disabled={isSubmitting || isPageLoading}
                >
                  {t("selfSignIn.footer.startOver")}
                </OpButton>
              </div>
            )}

            {/* No Matches Found - Only Start Over button */}
            {showNoMatchesButton() && (
              <div className="self-signin-footer centered">
                <OpButton
                  type="default"
                  size="large"
                  onClick={handleStartOver}
                  className="start-over-only"
                  disabled={isSubmitting || isPageLoading}
                >
                  {t("selfSignIn.footer.startOver")}
                </OpButton>
              </div>
            )}

            {/* Confirmation - Only Start Over button */}
            {showConfirmationButtons() && (
              <div className="self-signin-footer centered">
                <OpButton
                  type="primary"
                  size="large"
                  onClick={handleStartOver}
                  disabled={isSubmitting || isPageLoading}
                  className="start-over-only"
                >
                  {t("selfSignIn.footer.startOver")}
                </OpButton>
              </div>
            )}
          </>
        )}
      </Content>
    </Layout>
  );
};

export default SelfSignIn;
